import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, del, get, post, put } from '../api.js';
import { PageTitle } from '../frame.js';
import { t } from '../i18n.js';
import { useSession } from '../session.js';
import { fmtDateTime } from '../util.js';
import type { FeedSourceDto } from './feeds.js';
import { Dialog } from './rundown-dialogs.js';

interface Suggestion {
  name: string;
  url: string;
  groupName: string;
  language: string;
  browserAgent?: boolean;
}
interface Preview {
  title: string;
  link: string | null;
  count: number;
  items: Array<{ title: string; link: string | null; publishedAt: string | null; summary: string }>;
}
const errText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

/** Διαχείριση → Ροές: which feeds the newsroom reads, how often, and whether the last read worked. */
export function AdminFeedsPage() {
  const { can } = useSession();
  const [sources, setSources] = useState<FeedSourceDto[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [ap, setAp] = useState<{ configured: boolean; feeds: Array<{ name: string; url: string; description: string }>; error: string | null } | null>(null);
  const [editing, setEditing] = useState<FeedSourceDto | 'new' | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () =>
    Promise.all([get<{ sources: FeedSourceDto[] }>('/api/feeds/sources'), get<{ suggestions: Suggestion[] }>('/api/feeds/suggestions'), get<{ configured: boolean; feeds: Array<{ name: string; url: string; description: string }>; error: string | null }>('/api/feeds/ap')])
      .then(([s, g, a]) => {
        setSources(s.sources);
        setSuggestions(g.suggestions);
        setAp(a);
        setError(null);
      })
      .catch((e) => setError(errText(e)));
  useEffect(() => {
    void load();
  }, []);

  if (!can('admin.config')) return <div className="banner err">{t('noPermission')}</div>;

  const fetchNow = async (s: FeedSourceDto) => {
    setBusy(s.id);
    setNotice(null);
    try {
      const r = await post<{ poll: { status: string; inserted: number; total: number; error: string | null } }>(`/api/feeds/sources/${s.id}/fetch`);
      setNotice(r.poll.status === 'error' ? `${s.name}: ${r.poll.error}` : `${s.name}: ${r.poll.inserted} ${t('feedsNewItems')} (${r.poll.total} ${t('feedsInFeed')})`);
      await load();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(null);
    }
  };
  const toggle = async (s: FeedSourceDto) => {
    try {
      await put(`/api/feeds/sources/${s.id}`, { active: !s.active });
      await load();
    } catch (e) {
      setError(errText(e));
    }
  };
  const remove = async (s: FeedSourceDto) => {
    if (!window.confirm(`${t('feedsDeleteConfirm')} ${s.name}?`)) return;
    try {
      await del(`/api/feeds/sources/${s.id}`);
      await load();
    } catch (e) {
      setError(errText(e));
    }
  };

  return (
    <>
      <PageTitle title={t('feedsAdminTitle')} hint={t('feedsAdminHint')} />
      <div className="toolbar left">
        <span style={{ flex: 1 }} />
        <button className="btn primary" onClick={() => setEditing('new')}>
          ✚ {t('feedsNew')}
        </button>
      </div>
      {error && <div className="banner err">{error}</div>}
      {notice && <div className="banner info">{notice}</div>}
      <div className="table-scroll">
        <table className="grid">
          <thead>
            <tr>
              <th>{t('feedsGroup')}</th>
              <th>{t('feedsName')}</th>
              <th>URL</th>
              <th>{t('feedsInterval')}</th>
              <th>{t('feedsLastRead')}</th>
              <th>{t('feedsItems')}</th>
              <th>{t('status')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sources.length === 0 && (
              <tr>
                <td colSpan={8} className="c muted">
                  {t('feedsNoSources')}
                </td>
              </tr>
            )}
            {sources.map((s) => (
              <tr key={s.id} className={s.active ? '' : 'floated'}>
                <td>{s.groupName}</td>
                <td>
                  {s.name} <span className="muted small">{s.language}</span>
                </td>
                <td className="small" style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.url}>
                  {s.url}
                </td>
                <td className="num">{s.intervalMinutes}′</td>
                <td className="c small">{s.lastFetchedAt ? fmtDateTime(s.lastFetchedAt) : '—'}</td>
                <td className="num">{s.itemCount}</td>
                <td className="c">
                  {!s.active ? <span className="status warn">{t('inactive')}</span> : s.lastStatus === 'error' ? <span className="status err" title={s.lastError ?? ''}>{t('feedsError')}</span> : s.lastStatus ? <span className="status ok">OK</span> : <span className="status info">—</span>}
                  {s.lastError && s.active && <div className="small muted">{s.lastError}</div>}
                </td>
                <td className="actions">
                  <button title={t('feedsFetchNow')} disabled={busy === s.id} onClick={() => void fetchNow(s)}>
                    ↻
                  </button>
                  <button title={s.active ? t('feedsPause') : t('feedsResume')} onClick={() => void toggle(s)}>
                    {s.active ? '⏸' : '▶'}
                  </button>
                  <button title={t('edit')} onClick={() => setEditing(s)}>
                    ✎
                  </button>
                  <button title={t('delete')} onClick={() => void remove(s)}>
                    🗑
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="tip">{t('feedsAdminTip')}</p>
      <p className="tip">
        <b>{t('feedsAp')}</b> {!ap ? '…' : !ap.configured ? t('feedsApNotConfigured') : ap.error ? `${t('feedsApError')} (${ap.error})` : `${ap.feeds.length} ${t('feedsApFeeds')}`}
      </p>
      {editing && (
        <FeedDialog
          source={editing === 'new' ? null : editing}
          suggestions={[...suggestions, ...(ap?.feeds ?? []).map((f) => ({ name: `AP · ${f.name}`, url: f.url, groupName: 'Διεθνή', language: 'en' }))].filter((g) => !sources.some((s) => s.url === g.url))}
          onClose={() => setEditing(null)}
          onSaved={(msg) => {
            setEditing(null);
            setNotice(msg);
            void load();
          }}
        />
      )}
    </>
  );
}

function FeedDialog({ source, suggestions, onClose, onSaved }: { source: FeedSourceDto | null; suggestions: Suggestion[]; onClose: () => void; onSaved: (msg: string | null) => void }) {
  const [name, setName] = useState(source?.name ?? '');
  const [url, setUrl] = useState(source?.url ?? '');
  const [groupName, setGroupName] = useState(source?.groupName ?? 'Κύπρος');
  const [language, setLanguage] = useState(source?.language ?? 'el');
  const [interval, setInterval_] = useState(source?.intervalMinutes ?? 10);
  const [active, setActive] = useState(source?.active ?? true);
  const [browserAgent, setBrowserAgent] = useState(source?.browserAgent ?? false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState<'preview' | 'save' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = (g: Suggestion) => {
    setName(g.name);
    setUrl(g.url);
    setGroupName(g.groupName);
    setLanguage(g.language);
    setBrowserAgent(!!g.browserAgent);
    setPreview(null);
  };
  const doPreview = async () => {
    setBusy('preview');
    setError(null);
    try {
      setPreview(await post<Preview>('/api/feeds/preview', { url, browserAgent }));
    } catch (e) {
      setError(errText(e));
      setPreview(null);
    } finally {
      setBusy(null);
    }
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy('save');
    setError(null);
    try {
      const body = { name: name.trim(), url: url.trim(), groupName: groupName.trim(), language: language.trim(), intervalMinutes: interval, active, browserAgent };
      if (source) {
        await put(`/api/feeds/sources/${source.id}`, body);
        onSaved(null);
      } else {
        const r = await post<{ poll: { status: string; inserted: number; total: number; error: string | null } | null }>('/api/feeds/sources', body);
        onSaved(r.poll ? (r.poll.status === 'error' ? `${body.name}: ${r.poll.error}` : `${body.name}: ${r.poll.inserted} ${t('feedsNewItems')}`) : null);
      }
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(null);
    }
  };
  const ready = name.trim() && /^https?:\/\/\S+$/i.test(url.trim());

  return (
    <Dialog title={source ? `${t('feedsEdit')} — ${source.name}` : t('feedsNew')} onClose={onClose} width={720}>
      <form onSubmit={submit}>
        {!source && suggestions.length > 0 && (
          <div className="form-grid" style={{ gridTemplateColumns: 'max-content 1fr' }}>
            <label htmlFor="f-sug">{t('feedsSuggested')} :</label>
            <select
              id="f-sug"
              defaultValue=""
              onChange={(e) => {
                const g = suggestions.find((x) => x.url === e.target.value);
                if (g) pick(g);
              }}
            >
              <option value="">—</option>
              {suggestions.map((g) => (
                <option key={g.url} value={g.url}>
                  {g.groupName} · {g.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="form-grid" style={{ gridTemplateColumns: 'max-content 1fr max-content 1fr' }}>
          <label htmlFor="f-name">{t('feedsName')} :</label>
          <input id="f-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoFocus />
          <label htmlFor="f-group">{t('feedsGroup')} :</label>
          <input id="f-group" value={groupName} onChange={(e) => setGroupName(e.target.value)} maxLength={40} list="feed-groups" />
          <datalist id="feed-groups">
            {['Κύπρος', 'Ελλάδα', 'Ευρώπη', 'Διεθνή', 'Αθλητικά', 'Οικονομία'].map((g) => (
              <option key={g} value={g} />
            ))}
          </datalist>
          <label htmlFor="f-url">URL :</label>
          <input id="f-url" value={url} onChange={(e) => setUrl(e.target.value)} maxLength={2000} placeholder="https://…/rss" style={{ gridColumn: 'span 3' }} />
          <label htmlFor="f-lang">{t('language')} :</label>
          <input id="f-lang" value={language} onChange={(e) => setLanguage(e.target.value)} maxLength={8} style={{ width: 60 }} />
          <label htmlFor="f-int">{t('feedsInterval')} :</label>
          <span>
            <input id="f-int" type="number" min={2} max={1440} value={interval} onChange={(e) => setInterval_(Number(e.target.value) || 10)} style={{ width: 70 }} /> {t('minutes')}
          </span>
          <label htmlFor="f-active">{t('status')} :</label>
          <label>
            <input id="f-active" type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> {t('active')}
          </label>
          <label htmlFor="f-agent">{t('feedsAgent')} :</label>
          <label title={t('feedsAgentHint')}>
            <input id="f-agent" type="checkbox" checked={browserAgent} onChange={(e) => setBrowserAgent(e.target.checked)} /> {t('feedsAgentBrowser')}
          </label>
        </div>
        <div className="toolbar left">
          <button type="button" className="btn" disabled={!ready || busy !== null} onClick={() => void doPreview()}>
            👁 {t('feedsPreview')}
          </button>
          <button className="btn primary" disabled={!ready || busy !== null}>
            💾 {t('save')}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            {t('cancel')}
          </button>
          {error && <span className="status err">{error}</span>}
        </div>
        {preview && (
          <div className="panel" style={{ margin: '6px 14px' }}>
            <div className="panel-head">
              <span className="panel-title">
                {preview.title || '—'} · {preview.count} {t('feedsInFeed')}
              </span>
            </div>
            <ul className="feed-preview">
              {preview.items.map((i, k) => (
                <li key={k}>
                  <b>{i.title}</b>
                  {i.publishedAt && <span className="muted small"> · {fmtDateTime(i.publishedAt)}</span>}
                  <div className="small">{i.summary}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </form>
    </Dialog>
  );
}
